/**
 * Slim API route registry for the buy funnel. Only the routes the checkout actually hits.
 * If you find yourself dereferencing something not registered here, register it explicitly —
 * a missing helper throws TypeError at runtime, which is worse than a compile error.
 */
export default defineNuxtPlugin(() => {
    const routes = {
        me: {
            getMe: () => `/me`,
            financial: {
                // Finalizes a 3DS-authenticated PaymentIntent + flips the invoice to PAID
                // server-side. /me variant looks up the authed person.
                confirmPayment: () => `/me/financial/confirm-payment`,
            },
        },
        auth: {
            // POST — request a 6-digit email code (auto-creates a lead if the email is new).
            start: () => `/auth/start`,
            // POST — verify the code; returns Passport token + refresh_token. Needs credentials.
            verify: () => `/auth/verify`,
            // PATCH — set first/last name after login (only fills when currently empty).
            profile: () => `/auth/profile`,
        },
        licenses: {
            // GET — public consumer pricing (no auth) so logged-out buyers can see prices.
            standardCostsPublic: () => `/licenses/standard-costs`,
        },
        billing: {
            // GET — public Stripe publishable key for this environment (no auth). Paired
            // server-side with the secret key the backend charges, so the key the browser
            // loads Stripe with always matches the backend's account/mode.
            stripeConfig: () => `/billing/stripe-config`,
        },
        public: {
            // GET — public data-broker coverage counts (no auth): total brokers, custom-site
            // count, and per-category breakdown. Powers the plan cards' "Data Brokers Covered"
            // detail. Same endpoint the marketing site (privacybee-com) bakes at build time.
            companiesStats: () => `/public/companies/stats`,
        },
        people: {
            licenses: {
                // GET returns the buyer's owned-licenses collection (needed post-purchase to
                // find the freshly created license for gift-mode auto-assign).
                get_licenses: (personId: number) => `/people/${personId}/licenses`,
                // POST creates the purchase. Same path as get_licenses.
                purchase_licenses: (personId: number) => `/people/${personId}/licenses`,
                // PATCH to assign / unassign / set renew flag on a specific license.
                update_license: (personId: number, licenseId: number) => `/people/${personId}/licenses/${licenseId}`,
                // PATCH to change plan/term on an existing license (the modify/upgrade flow).
                change_license: (personId: number, licenseId: number) => `/people/${personId}/licenses/${licenseId}/change`,
                // POST to get the prorated breakdown for an upgrade (License Credit, Annual
                // Discount, Multi-license Discount, Due Today, etc.).
                calculate_upgrade_price: (personId: number, licenseId: number) => `/people/${personId}/licenses/${licenseId}/calculate-upgrade-price`,
            },
            billing: {
                creditBalance: (personId: number) => `/people/${personId}/financial/credit-balance`,
                giftCertificates: {
                    apply: (personId: number) => `/people/${personId}/financial/gift-certificates/apply`,
                },
            },
        },
    };

    return {
        provide: {
            api: { routes },
        },
    };
});
